import React from "react";
import { useTranslation } from "react-i18next";
import { useEditEvent } from "@planzone/view-models";
import { DateTimePicker } from "@common/dateTimePicker";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import { Props } from "./types";
import styles from "./styles.scss";

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function DatesPickers({ event, error, readOnly }: Props) {
  const { t } = useTranslation();
  const workspace = useWorkspaceContext();
  const projectTimezone =
    workspace.type === "project" ? workspace.project.timeZone : undefined;
  const [startDate, setStartDate] = React.useState(event.startTime);
  const [endDate, setEndDate] = React.useState(event.endTime);
  const start = new Date(startDate || new Date());
  const end = new Date(endDate || new Date());
  const { editEvent } = useEditEvent(event);

  React.useEffect(() => {
    setStartDate(event.startTime);
  }, [event.startTime]);

  React.useEffect(() => {
    setEndDate(event.endTime);
  }, [event.endTime]);

  return (
    <div className={styles.datesWrapper}>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("retrieveEvent.startTime")}</h3>
        <div className={styles.input}>
          <DateTimePicker
            projectTimezone={projectTimezone}
            dateTime={startDate}
            placeholder={t("retrieveEvent.startTime")}
            isInvalid={error.startDateOutOfBound || error.invalidDateRange}
            readOnly={readOnly}
            minDate={
              event.minStartDate
                ? {
                    year: new Date(event.minStartDate).getFullYear(),
                    month: new Date(event.minStartDate).getMonth(),
                    day: new Date(event.minStartDate).getDate(),
                  }
                : undefined
            }
            maxDate={(() => {
              const bound = endDate || event.maxEndDate;
              return bound
                ? {
                    year: new Date(bound).getFullYear(),
                    month: new Date(bound).getMonth(),
                    day: new Date(bound).getDate(),
                  }
                : undefined;
            })()}
            input={(date) => {
              const nextDate = new Date(date);

              if (
                start.getDate() !== nextDate.getDate() ||
                start.getHours() !== nextDate.getHours() ||
                start.getMinutes() !== nextDate.getMinutes()
              ) {
                const adjustedEnd =
                  endDate && isSameDay(nextDate, end) && nextDate >= end
                    ? new Date(
                        nextDate.getTime() + 60 * 60 * 1000,
                      ).toISOString()
                    : endDate;

                editEvent({
                  event,
                  nextEvent: {
                    ...event,
                    startTime: nextDate.toISOString(),
                    endTime: adjustedEnd,
                  },
                });
              } else {
                setStartDate(nextDate.toISOString());
              }
            }}
            clear={() =>
              editEvent({
                event,
                nextEvent: { ...event, startTime: "" },
              })
            }
            today={() => {
              const now = new Date();
              editEvent({
                event,
                nextEvent: { ...event, startTime: now.toISOString() },
              });
            }}
          />
        </div>
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("retrieveEvent.endTime")}</h3>
        <div className={styles.input}>
          <DateTimePicker
            projectTimezone={projectTimezone}
            dateTime={endDate}
            placeholder={t("retrieveEvent.endTime")}
            isInvalid={error.startDateOutOfBound || error.invalidDateRange}
            readOnly={readOnly}
            minDate={(() => {
              const bound = startDate || event.minStartDate;
              return bound
                ? {
                    year: new Date(bound).getFullYear(),
                    month: new Date(bound).getMonth(),
                    day: new Date(bound).getDate(),
                  }
                : undefined;
            })()}
            input={(date) => {
              const nextDate = new Date(date);

              if (
                end.getDate() !== nextDate.getDate() ||
                end.getHours() !== nextDate.getHours() ||
                end.getMinutes() !== nextDate.getMinutes()
              ) {
                const adjustedStart =
                  startDate && isSameDay(nextDate, start) && nextDate <= start
                    ? new Date(
                        nextDate.getTime() - 60 * 60 * 1000,
                      ).toISOString()
                    : startDate;

                editEvent({
                  event,
                  nextEvent: {
                    ...event,
                    startTime: adjustedStart,
                    endTime: nextDate.toISOString(),
                  },
                });
              } else {
                setEndDate(nextDate.toISOString());
              }
            }}
            clear={() =>
              editEvent({
                event,
                nextEvent: { ...event, endTime: "" },
              })
            }
            today={() => {
              const now = new Date();
              editEvent({
                event,
                nextEvent: { ...event, endTime: now.toISOString() },
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}

export { DatesPickers };
